"use client";
import * as React from "react";
import { useERP } from "@/lib/erp/store";
import { ALL_PAGES } from "@/lib/erp/modulesV2";
import { FullDetailPage } from "./FullDetailPage";
import { FullPageFormPage } from "./FullPageFormPage";
import { GenericListPage } from "./GenericListPage";
import { ReportPage, type ReportType } from "./ReportPage";
import { SettingsPage } from "./SettingsPage";
import { ExecutiveControlTower } from "./ExecutiveControlTower";

import { ProcurementModule } from "./modules/ProcurementModule";
import { SalesModule } from "./modules/SalesModule";
import { InventoryModule } from "./modules/InventoryModule";
import { FinanceModule } from "./modules/FinanceModule";
import { ReturnsModule } from "./modules/ReturnsModule";
import { MasterDataModule } from "./modules/MasterDataModule";
import { ApprovalsModule } from "./modules/ApprovalsModule";
import { POSModule } from "./modules/POSModule";
import { RealtimeTrackingPage } from "./modules/RealtimeTrackingPage";
import { InternalChatPage } from "./modules/InternalChatPage";
import { ComprehensiveSettingsPage } from "./modules/ComprehensiveSettingsPage";
import { EcommerceModule, ManufacturingModule, ProjectSalesModule, ThreePLModule, ConsignmentModule, ESGModule } from "./modules/IndustrialModules";
import { HRModule } from "./modules/HRModule";
import { BIDashboardModule } from "./modules/BIDashboardModule";
import { AICopilotModule } from "./modules/AICopilotModule";
import { CRModule } from "./modules/CRModule";
import { DMSModule } from "./modules/DMSModule";
import { SFAModule } from "./modules/SFAModule";
import { TMSModule } from "./modules/TMSModule";
import { WMSModule } from "./modules/WMSModule";
import { VATTaxModule } from "./modules/VATTaxModule";
import { CreditControlModule } from "./modules/CreditControlModule";
import { SecurityModule } from "./modules/SecurityModule";

export interface PageRouterProps {
  pageId: string;
  onNavigate: (pageId: string) => void;
  onBack: () => void;
}

export function PageRouter({ pageId, onNavigate, onBack }: PageRouterProps) {
  // Detail pages
  if (pageId.startsWith("po-detail:")) {
    const id = pageId.slice("po-detail:".length);
    return <FullDetailPage type="po" id={id} onBack={onBack} onNavigate={onNavigate} />;
  }
  if (pageId.startsWith("so-detail:")) {
    const id = pageId.slice("so-detail:".length);
    return <FullDetailPage type="so" id={id} onBack={onBack} onNavigate={onNavigate} />;
  }
  if (pageId.startsWith("grn-detail:")) {
    const id = pageId.slice("grn-detail:".length);
    return <FullDetailPage type="grn" id={id} onBack={onBack} onNavigate={onNavigate} />;
  }
  if (pageId.startsWith("invoice-detail:")) {
    const id = pageId.slice("invoice-detail:".length);
    return <FullDetailPage type="invoice" id={id} onBack={onBack} onNavigate={onNavigate} />;
  }
  if (pageId.startsWith("receipt-detail:")) {
    const id = pageId.slice("receipt-detail:".length);
    return <FullDetailPage type="receipt" id={id} onBack={onBack} onNavigate={onNavigate} />;
  }
  if (pageId.startsWith("rma-detail:")) {
    const id = pageId.slice("rma-detail:".length);
    return <FullDetailPage type="rma" id={id} onBack={onBack} onNavigate={onNavigate} />;
  }

  // Form pages — handle both new and ?edit=ID edit modes (C2 fix)
  if (pageId.startsWith("md-products-new")) {
    const editId = new URLSearchParams(pageId.split("?")[1] || "").get("edit") || undefined;
    return <FullPageFormPage formType="product" onBack={onBack} editId={editId} />;
  }
  if (pageId.startsWith("md-customers-new")) {
    const editId = new URLSearchParams(pageId.split("?")[1] || "").get("edit") || undefined;
    return <FullPageFormPage formType="customer" onBack={onBack} editId={editId} />;
  }
  if (pageId.startsWith("md-suppliers-new")) {
    const editId = new URLSearchParams(pageId.split("?")[1] || "").get("edit") || undefined;
    return <FullPageFormPage formType="supplier" onBack={onBack} editId={editId} />;
  }
  if (pageId.startsWith("md-warehouses-new")) {
    const editId = new URLSearchParams(pageId.split("?")[1] || "").get("edit") || undefined;
    return <FullPageFormPage formType="warehouse" onBack={onBack} editId={editId} />;
  }
  if (pageId === "po-new") return <FullPageFormPage formType="po" onBack={onBack} />;
  if (pageId === "so-new") return <FullPageFormPage formType="so" onBack={onBack} />;
  if (pageId === "returns-new") return <FullPageFormPage formType="rma" onBack={onBack} />;

  // Module-level navigation routes (from modulesV2.ts registry)
  if (pageId === "po-list" || pageId === "po-requisitions") return <ProcurementModule onNavigate={onNavigate} />;
  if (pageId === "grn-list") return <ProcurementModule onNavigate={onNavigate} />;
  if (pageId === "po-report-supplier") return <ReportPage reportType="supplier-performance" />;

  if (pageId === "inv-stock-list" || pageId === "inv-stock-ledger" || pageId === "inv-stock-warehouse" || pageId === "inv-reorder-list") return <InventoryModule />;
  if (pageId === "inv-report-aging") return <ReportPage reportType="stock-aging" />;
  if (pageId === "inv-report-valuation") return <ReportPage reportType="stock-valuation" />;

  if (pageId === "wms-inbound" || pageId === "wms-picking" || pageId === "wms-dispatch") return <WMSModule />;

  if (pageId === "so-list") return <SalesModule onNavigate={onNavigate} />;
  if (pageId === "inv-list") return <SalesModule onNavigate={onNavigate} />;
  if (pageId === "rct-list") return <SalesModule onNavigate={onNavigate} />;
  if (pageId === "so-report-summary") return <ReportPage reportType="sales-summary" />;
  if (pageId === "so-report-margin") return <ReportPage reportType="margin-analysis" />;

  if (pageId === "returns-list") return <ReturnsModule onNavigate={onNavigate} />;

  if (pageId === "fin-je-list" || pageId === "fin-ar-aging" || pageId === "fin-ap-list") return <FinanceModule />;
  if (pageId === "fin-tb") return <FinanceModule />;
  if (pageId === "fin-report-pl") return <ReportPage reportType="pnl" />;
  if (pageId === "fin-report-bs") return <ReportPage reportType="balance-sheet" />;
  if (pageId === "fin-report-cash") return <ReportPage reportType="cash-flow" />;

  if (pageId === "vat-ledger" || pageId === "vat-report") return <VATTaxModule />;
  if (pageId === "credit-list") return <CreditControlModule />;

  if (pageId === "wf-pending" || pageId === "wf-history") return <ApprovalsModule />;

  if (pageId === "bi-ceo" || pageId === "bi-sales") return <BIDashboardModule />;
  if (pageId === "track-map" || pageId === "track-salesman" || pageId === "track-orders" || pageId === "track-zones") return <RealtimeTrackingPage />;
  if (pageId === "chat") return <InternalChatPage />;
  if (pageId === "bi-custom") return <ReportPage reportType="custom-builder" />;

  if (pageId === "ai-copilot" || pageId === "ai-forecast") return <AICopilotModule />;

  if (pageId === "budget-list") return <GenericListPage entityType="budget" onNavigate={onNavigate} title="Budget vs Actual" />;
  if (pageId === "mkt-list") return <GenericListPage entityType="marketing" onNavigate={onNavigate} title="Marketing Campaigns" />;
  if (pageId === "portal-list") return <GenericListPage entityType="portal" onNavigate={onNavigate} title="B2B Portal Orders" />;
  if (pageId === "pay-list") return <GenericListPage entityType="payment" onNavigate={onNavigate} title="Collection Register" />;
  if (pageId === "sup-tickets") return <GenericListPage entityType="support" onNavigate={onNavigate} title="Support Tickets" />;
  if (pageId === "doc-list") return <GenericListPage entityType="document" onNavigate={onNavigate} title="Document Repository" />;
  if (pageId === "api-list") return <GenericListPage entityType="api" onNavigate={onNavigate} title="API Catalogue" />;
  if (pageId === "mob-list") return <GenericListPage entityType="mobile" onNavigate={onNavigate} title="Mobile Devices" />;
  if (pageId === "qc-list") return <GenericListPage entityType="quality" onNavigate={onNavigate} title="Quality Inspections" />;

  if (pageId === "dms-list") return <DMSModule />;
  if (pageId === "sfa-visits" || pageId === "sfa-beat") return <SFAModule />;
  if (pageId === "crm-360" || pageId === "crm-tickets") return <CRModule />;
  if (pageId === "tms-trips" || pageId === "tms-fleet") return <TMSModule />;

  if (pageId === "sec-roles" || pageId === "sec-audit") return <SecurityModule />;

  // ===== HR — all pages route to proper HRModule =====
  if (["hr-emp-list","hr-emp-new","hr-emp-attendance","hr-payroll-list","hr-payroll-run","hr-payslip","hr-appraisal","hr-kpi"].includes(pageId)) return <HRModule pageId={pageId} onNavigate={onNavigate} />;

  if (pageId === "ind-list") return <GenericListPage entityType="industry" onNavigate={onNavigate} title="Industry Modules" />;
  if (pageId === "ind-pos") return <POSModule />;
  if (pageId === "ind-ecom") return <EcommerceModule />;
  if (pageId === "ind-mfg") return <ManufacturingModule />;
  if (pageId === "ind-project") return <ProjectSalesModule />;
  if (pageId === "ind-3pl") return <ThreePLModule />;
  if (pageId === "ind-consign") return <ConsignmentModule />;
  if (pageId === "ind-esg") return <ESGModule />;

  if (pageId === "md-products-list") return <MasterDataModule onNavigate={onNavigate} />;
  if (pageId === "md-products-import") return <GenericListPage entityType="product" onNavigate={onNavigate} title="Product Import / Export" />;
  if (pageId === "md-customers-list") return <MasterDataModule onNavigate={onNavigate} />;
  if (pageId === "md-suppliers-list") return <MasterDataModule onNavigate={onNavigate} />;
  if (pageId === "md-warehouses-list") return <MasterDataModule onNavigate={onNavigate} />;
  if (pageId === "md-org-setup") return <SettingsPage title="Organization Setup" description="Configure your company profile." />;

  if (pageId === "adm-settings") return <ComprehensiveSettingsPage initialTab="org" />;
  if (pageId === "adm-data" || pageId === "adm-backup" || pageId === "adm-import-export" || pageId === "adm-numbering" || pageId === "adm-localization") return <ComprehensiveSettingsPage />;

  // ===== Master Data — all new pages route to MasterDataModule or GenericListPage =====
  if (pageId.startsWith("md-products") || pageId.startsWith("md-customers") || pageId.startsWith("md-suppliers") || pageId.startsWith("md-warehouses")) return <MasterDataModule onNavigate={onNavigate} />;
  if (pageId.startsWith("md-org") || pageId.startsWith("md-pricing") || pageId.startsWith("md-gov")) return <SettingsPage title="Configuration" description="System configuration page." />;

  // ===== All settings-type pages → ComprehensiveSettingsPage =====
  if (["vat-rates","vat-rules","vat-effective","credit-policy","wf-templates","wf-delegation","wf-sla","ai-models","ai-audit","sec-mfa","inv-safety-stock","inv-replenish-rules","wms-putaway-rules","doc-signature","portal-permissions","tms-delivery-window","fin-report-closing","fin-coa"].includes(pageId)) return <ComprehensiveSettingsPage />;

  // ===== Procurement — new pages =====
  if (["pr-list","pr-new","rfq-list","rfq-new","rfq-compare","pinv-list","pinv-3way","import-lc","import-customs","po-blanket"].includes(pageId)) return <ProcurementModule onNavigate={onNavigate} />;
  if (pageId === "grn-new") return <ProcurementModule onNavigate={onNavigate} />;
  if (pageId === "po-report-lead") return <ReportPage reportType="stock-aging" />;
  if (pageId === "po-report-ppv") return <ReportPage reportType="sales-summary" />;
  if (pageId === "po-report-3way") return <ReportPage reportType="supplier-performance" />;
  if (pageId === "import-landed") return <ReportPage reportType="stock-valuation" />;

  // ===== Inventory — new pages =====
  if (["inv-stock-atp","inv-stock-transfer","inv-batch-list","inv-serial-list","inv-expiry","inv-adj-list","inv-adj-new","inv-cycle-count"].includes(pageId)) return <InventoryModule />;
  if (pageId === "inv-report-dead") return <ReportPage reportType="stock-aging" />;
  if (pageId === "inv-report-movement") return <ReportPage reportType="sales-summary" />;

  // ===== WMS — new pages =====
  if (["wms-inbound-list","wms-inbound-scan","wms-qc","wms-putaway-list","wms-picking-list","wms-picking-new","wms-picking-wave","wms-packing","wms-loading","wms-report-productivity","wms-report-utilization"].includes(pageId)) return <WMSModule />;

  // ===== Sales — new pages =====
  if (["quote-list","quote-new","so-backorder","inv-new","inv-credit","rct-new","rct-unalloc"].includes(pageId)) return <SalesModule onNavigate={onNavigate} />;
  if (pageId === "so-report-customer") return <ReportPage reportType="sales-summary" />;
  if (pageId === "so-report-dso") return <ReportPage reportType="stock-aging" />;

  // ===== DMS — new pages =====
  if (["dms-new","dms-hierarchy","dms-outlets","dms-routes","dms-journey","dms-primary","dms-secondary","dms-tertiary"].includes(pageId)) return <DMSModule />;
  if (["dms-report-territory","dms-report-coverage","dms-report-scheme"].includes(pageId)) return <ReportPage reportType="sales-summary" />;

  // ===== SFA — new pages =====
  if (["sfa-visits-list","sfa-checkin","sfa-beat-list","sfa-beat-new","sfa-orders-list","sfa-collection","sfa-expense-list","sfa-expense-new","sfa-report-productivity","sfa-report-coverage"].includes(pageId)) return <SFAModule />;

  // ===== Pricing — new module =====
  if (["pricing-list","pricing-new","scheme-list","scheme-new","scheme-claims","pricing-report-roi","pricing-report-leakage"].includes(pageId)) return <GenericListPage entityType="pricing" onNavigate={onNavigate} title="Pricing & Schemes" />;

  // ===== CRM — new pages =====
  if (["crm-segmentation","crm-lifecycle","crm-leads","crm-opportunities","crm-pipeline-view","crm-ticket-new","crm-report-satisfaction","crm-report-churn"].includes(pageId)) return <CRModule />;

  // ===== TMS — new pages =====
  if (["tms-trip-new","tms-pod","tms-drivers","tms-maintenance","tms-route-opt","tms-report-ontime","tms-report-cost"].includes(pageId)) return <TMSModule />;

  // ===== Returns — new pages =====
  if (["returns-inspect","returns-grade","returns-credit","returns-replace","returns-report-rate","returns-report-reason"].includes(pageId)) return <ReturnsModule onNavigate={onNavigate} />;

  // ===== Finance — new pages =====
  if (["fin-je-new","fin-ar-statement","fin-ap-payment","fin-cashbook","fin-bank","fin-cheque","fin-asset-register","fin-asset-dep"].includes(pageId)) return <FinanceModule />;

  // ===== VAT — new pages =====
  if (["vat-mushak","vat-recon","vat-ait"].includes(pageId)) return <VATTaxModule />;

  // ===== Credit — new pages =====
  if (["credit-hold","credit-collection","credit-forecast","credit-report-risk","credit-report-baddebt"].includes(pageId)) return <CreditControlModule />;

  // ===== HR — already handled above =====

  // ===== Workflow — new pages =====
  // (already handled above)

  // ===== BI — new pages =====
  if (["bi-supply","bi-finance","bi-saved"].includes(pageId)) return <BIDashboardModule />;

  // ===== AI — new pages =====
  if (["ai-anomaly","ai-ocr"].includes(pageId)) return <AICopilotModule />;

  // ===== Budget — new pages =====
  if (["budget-new","budget-cost-center","budget-profitability"].includes(pageId)) return <GenericListPage entityType="budget" onNavigate={onNavigate} title="Budget & Costing" />;

  // ===== Marketing — new pages =====
  if (["mkt-new","mkt-loyalty","mkt-rewards"].includes(pageId)) return <GenericListPage entityType="marketing" onNavigate={onNavigate} title="Marketing" />;

  // ===== Portal — new pages =====
  if (["portal-track","portal-users"].includes(pageId)) return <GenericListPage entityType="portal" onNavigate={onNavigate} title="B2B Portal" />;

  // ===== Payment — new pages =====
  if (["pay-digital","pay-bank-recon","pay-cheque"].includes(pageId)) return <GenericListPage entityType="payment" onNavigate={onNavigate} title="Payment & Banking" />;

  // ===== Support — new pages =====
  if (["sup-ticket-new","sup-kb","sup-sop"].includes(pageId)) return <GenericListPage entityType="support" onNavigate={onNavigate} title="Support Desk" />;

  // ===== Documents — new pages =====
  if (["doc-upload","doc-templates"].includes(pageId)) return <GenericListPage entityType="document" onNavigate={onNavigate} title="Documents" />;

  // ===== Security — new pages =====
  if (["sec-users","sec-incidents"].includes(pageId)) return <SecurityModule />;

  // ===== API — new pages =====
  if (["api-docs","api-integrations","api-webhooks","api-health"].includes(pageId)) return <GenericListPage entityType="api" onNavigate={onNavigate} title="API & Integration" />;

  // ===== Mobile — new pages =====
  if (["mob-sync","mob-offline"].includes(pageId)) return <GenericListPage entityType="mobile" onNavigate={onNavigate} title="Mobile Apps" />;

  // ===== Quality — new pages =====
  if (["qc-new","qc-trace","qc-recall"].includes(pageId)) return <GenericListPage entityType="quality" onNavigate={onNavigate} title="Quality & Trace" />;

  // Executive dashboard home
  if (pageId === "home" || pageId === "" || pageId === "exec-tower") return <ExecutiveControlTower />;

  // Default: show a friendly placeholder with the page info
  const known = ALL_PAGES[pageId];
  if (known) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <div className="text-4xl opacity-20">{React.createElement(known.page.icon, { className: "size-16" })}</div>
        <h1 className="text-xl font-semibold">{known.page.label}</h1>
        <p className="text-sm text-muted-foreground max-w-md text-center">This page is part of the {known.moduleId} module. Full implementation coming soon.</p>
      </div>
    );
  }
  return <ExecutiveControlTower />;
}
